// client/src/components/leagues/ChatDrawer.js
import React from 'react'
import PropTypes from 'prop-types'

/**
 * ChatDrawer - Inline chat drawer UI for direct messaging with opponent
 * Displays as a side panel with message list and composer
 */
export default function ChatDrawer({
  isOpen,
  onClose,
  channel,
  messages,
  loading,
  error,
  sending,
  input,
  onInputChange,
  onSend,
  onOpenFull,
  currentUserId,
  opponentName,
}) {
  return (
    <div
      className={`chat-drawer ${isOpen ? 'open' : ''}`}
      role="dialog"
      aria-label="Direct message with opponent"
    >
      <div className="chat-drawer-header">
        <h6 className="chat-drawer-title mb-0 d-flex align-items-center">
          <span className="me-2" aria-hidden="true">
            💬
          </span>
          {opponentName ? `Chat with ${opponentName}` : 'Direct Message'}
        </h6>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-sm btn-light"
            onClick={onOpenFull}
            disabled={!channel}
          >
            Open Full Chat
          </button>
          <button
            className="btn btn-sm btn-outline-light"
            onClick={onClose}
            aria-label="Close chat"
          >
            ✕
          </button>
        </div>
      </div>
      <div className="chat-drawer-body">
        {loading ? (
          <div className="chat-drawer-empty">Connecting…</div>
        ) : error ? (
          <div className="alert alert-warning" role="alert">
            {error}
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-drawer-empty">No messages yet. Say hi!</div>
        ) : (
          messages.map((m) => {
            const isOwn = m.sender_player_id === currentUserId
            return (
              <div key={m.id} className={`chat-message ${isOwn ? 'own' : ''}`}>
                <div className="meta">
                  <span className="name">
                    {isOwn
                      ? 'You'
                      : `${m.firstname} ${m.lastname || ''}`.trim()}
                  </span>
                  <span className="time text-muted small">
                    {new Date(m.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="bubble">{m.body}</div>
              </div>
            )
          })
        )}
      </div>
      <div className="chat-drawer-footer">
        <div className="chat-composer">
          <textarea
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            placeholder={channel ? 'Type a message…' : 'Chat unavailable'}
            disabled={!channel || sending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onSend()
              }
            }}
          />
          <button
            className="btn btn-primary send-btn"
            disabled={!channel || sending || input.trim().length === 0}
            onClick={onSend}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}

ChatDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  channel: PropTypes.string,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      sender_player_id: PropTypes.number.isRequired,
      body: PropTypes.string.isRequired,
      created_at: PropTypes.string.isRequired,
      firstname: PropTypes.string,
      lastname: PropTypes.string,
    }),
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  sending: PropTypes.bool.isRequired,
  input: PropTypes.string.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onOpenFull: PropTypes.func.isRequired,
  currentUserId: PropTypes.number,
  opponentName: PropTypes.string,
}
